import axios from "../../../../../../axios";
import { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./Validation.module.scss";
import update from "immutability-helper";
import HideSVG from "../../../../../../assets/svgs/hide.svg";
import EditSVG from "../../../../../../assets/svgs/edit.svg";
import { getValidations, mergeValidation, moveValidationOptionApi } from "../../../../../../services/validations.service";
import { CircularProgress, Autocomplete, TextField, Popover, Button, Typography, Box } from "@mui/material";
import useResetDynamicCards from "../../../../../hooks/useResetDynamicCards";
import _ from "lodash";
import { QUESTION_INPUT_TYPE } from "../../../../../../types/question.type";
import { ADD_NOTIFICATION } from "../../../../../../store/types";
import useFacilityId from "../../../../../../hooks/useFacilityId";

const Validation = props => {
  const { allQuestions = [], readOnly = false } = props;

  const allQuestionsList = allQuestions
    ?.map(item => item?.question)
    ?.filter(question =>
      (question?.customQuestionInputType === QUESTION_INPUT_TYPE.UNLIMITED_ANSWERS ||
        question?.type === "validation") &&
      question?._id?.toString() !== props.questionId?.toString()
    ) || [];
  const { auth } = useSelector(({ auth }) => ({ auth }));
  const [searchValue, setSearchValue] = useState("");
  const [list, setList] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [showList, setShowList] = useState(false);
  const [editId, setEditId] = useState(undefined);
  const [editValue, setEditValue] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [getListLoading, setGetListLoading] = useState(false);
  const [showConfirmEdit, setShowConfirmEdit] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [selectedMergeOption, setSelectedMergeOption] = useState("");
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveLoading, setMoveLoading] = useState(false);
  const [selectedTargetQuestion, setSelectedTargetQuestion] = useState("");
  const [hideConfirmationAnchor, setHideConfirmationAnchor] = useState(null);
  const [itemToHide, setItemToHide] = useState(null);
  const resetDynamicCards = useResetDynamicCards();
  const activeFacilities = useSelector((state) => state.activeFacilities.facilities);

  // Add confirmation dialog states
  const [showMergeConfirmation, setShowMergeConfirmation] = useState(false);
  const [showMoveConfirmation, setShowMoveConfirmation] = useState(false);
  const [mergeConfirmationData, setMergeConfirmationData] = useState(null);
  const [moveConfirmationData, setMoveConfirmationData] = useState(null);

  // Refs to track previous values and prevent infinite loops
  const prevPropsValueRef = useRef();
  const prevListRef = useRef();
  const isUpdatingRef = useRef(false);
  const selectedValuesRef = useRef(selectedValues);
  
  // Update ref whenever selectedValues changes
  useEffect(() => {
    selectedValuesRef.current = selectedValues;
  }, [selectedValues]);

  const dispatch = useDispatch();

  const { facilityId: facilityIdFromHook } = useFacilityId();
  const facilityId = useMemo(() => {
    const selectedItem = props?.selectedItem;
    const hasMultipleFacilities = activeFacilities.length > 1;

    // Helper function to extract facility ID from different possible structures
    const extractFacilityId = (item) => {
      if (!item) return null;

      // Try facility._id first
      if (item.facility?._id) {
        return item.facility._id;
      }

      // Handle facilityId - could be an object with _id or just the ID string
      if (item.facilityId) {
        // If facilityId is an object with _id property
        if (typeof item.facilityId === 'object' && item.facilityId._id) {
          return item.facilityId._id;
        }
        // If facilityId is just the ID string
        if (typeof item.facilityId === 'string') {
          return item.facilityId;
        }
      }

      return null;
    };

    if (hasMultipleFacilities) {
      return extractFacilityId(selectedItem);
    }

    // For single facility, try selectedItem first, then fallback to localStorage
    const extractedId = extractFacilityId(selectedItem);
    return extractedId || facilityIdFromHook;
  }, [props?.selectedItem, activeFacilities?.length, facilityIdFromHook]);

  const getList = async () => {
    setGetListLoading(true)
    let sortedList = [];
    if (props.type) {
      sortedList = await getValidations({
        type: props.type,
        facilityid: facilityId,
        validationBase: props.validationBase
      })
    }
    
    let mergedList = sortedList?.length > 0 ? _.orderBy(sortedList, [item => (item?.label || '').toLowerCase()], ['asc']) : [];
    
    // Add extra options from dataInputLabel if available
    if (props.dataInputLabel) {
      const extraOptions = props.dataInputLabel.split(',').map(opt => {
        const trimmed = opt.trim();
        if (!trimmed) return null;
        return {
          _id: trimmed,
          label: trimmed,
          active: true,
          isExtra: true
        };
      }).filter(Boolean);
      
      // Merge and deduplicate by label/id
      const extraOptionsToAdd = extraOptions.filter(extra => !mergedList.find(l => l.label === extra.label || l._id === extra._id));
      mergedList = [...mergedList, ...extraOptionsToAdd];
      
      // Re-sort
      mergedList = _.orderBy(mergedList, [item => (item?.label || '').toLowerCase()], ['asc']);
    }

    setList(mergedList);
    setGetListLoading(false)

    // Update selected values based on props.value or current selectedValues
    if (props.value) {
      if (props.multiple) {
        setSelectedValues(props?.value?.map(val => {
          let found = mergedList.find(l => l._id === val);
          if (!found) found = mergedList.find(l => l.label === val);
          return found;
        }).filter(Boolean));
      } else {
        let foundItem = mergedList.find(l => l._id === props.value);
        if (!foundItem) foundItem = mergedList.find(l => l.label === props.value);
        setSelectedValues(foundItem ? [foundItem] : []);
      }
    } else if (selectedValues.length > 0) {
      // If no props.value but we have selectedValues, validate they still exist in the list
      const validSelectedValues = selectedValues.filter(val =>
        val && mergedList.find(l => l._id === val._id)
      );

      // If some selected values are no longer valid, update the state
      if (validSelectedValues.length !== selectedValues.length) {
        setSelectedValues(validSelectedValues);

        // Notify parent component about the change
        if (props.onChange) {
          if (props.multiple) {
            props.onChange(validSelectedValues);
          } else {
            props.onChange(validSelectedValues.length > 0 ? validSelectedValues[0]._id : null);
          }
        }
      }
    }
  };

  useEffect(() => {
    getList();
    // eslint-disable-next-line
  }, [props.type, props.dataInputLabel, facilityId]);

  // Watch for changes in props.value and update selected values
  useEffect(() => {
    // Prevent infinite loops by checking if we're currently updating
    if (isUpdatingRef.current) {
      return;
    }

    // Check if props.value or list actually changed
    const propsValueChanged = JSON.stringify(prevPropsValueRef.current) !== JSON.stringify(props.value);
    const listChanged = JSON.stringify(prevListRef.current) !== JSON.stringify(list);
    
    if (!propsValueChanged && !listChanged) {
      return;
    }

    // Update refs
    prevPropsValueRef.current = props.value;
    prevListRef.current = list;

    if (props.value && list.length > 0) {
      if (props.multiple) {
        const newSelectedValues = props?.value?.map(val => {
          // Check for standard ID match
          let found = list.find(l => l._id === val);
          // If not found, check for label match (useful for string-based extra options)
          if (!found) found = list.find(l => l.label === val);
          return found;
        }).filter(Boolean);
        // Only update if the values are actually different
        if (JSON.stringify(newSelectedValues) !== JSON.stringify(selectedValuesRef.current)) {
          isUpdatingRef.current = true;
          setSelectedValues(newSelectedValues);
          setTimeout(() => { isUpdatingRef.current = false; }, 0);
        }
      } else {
        let foundItem = list.find(l => l._id === props.value);
        if (!foundItem) foundItem = list.find(l => l.label === props.value);
        const newSelectedValues = foundItem ? [foundItem] : [];
        // Only update if the values are actually different
        if (JSON.stringify(newSelectedValues) !== JSON.stringify(selectedValuesRef.current)) {
          isUpdatingRef.current = true;
          setSelectedValues(newSelectedValues);
          setTimeout(() => { isUpdatingRef.current = false; }, 0);
        }
      }
    } else if (!props.value && selectedValuesRef.current.length > 0) {
      isUpdatingRef.current = true;
      setSelectedValues([]);
      setTimeout(() => { isUpdatingRef.current = false; }, 0);
    }
  }, [props.value, list, props.multiple]); // Remove selectedValues from dependencies

  const isSelected = value => {
    return selectedValues?.find(item => item?._id === value) ? true : false;
  };

  const updateValidation = async (id, value) => {
    if (!loading) {
      setLoading(true);
      let validation = await axios.put(`/api/validation/${id}`, { ...value });
      if (validation) {
        // eslint-disable-next-line eqeqeq
        setList(previousListData =>
          previousListData.map((data) =>
            data._id === id ? { ...data, active: false } : data
          )
        )
      }
      setLoading(false);
    }
  };

  const addValue = async () => {
    if (!searchValue) return;
    let newVal;
    let exists = list.find(item => item?.label === searchValue);
    if (!exists) {
      if (!loading) {
        setLoading(true);
        newVal = await axios.post(`/api/validation/`, {
          facilityId: facilityId,
          label: searchValue,
          active: true,
          type: props.type,
          createdBy: auth._id,
        });
        setLoading(false);
        setList(update(list, { $push: [newVal.data] }));

        if (props?.isCustomQuestion) {
          resetDynamicCards();
        }
      }
    } else {
      if (!loading) {
        setLoading(true);
        newVal = await axios.put(`/api/validation/${exists._id}`, {
          active: true,
        });
        setLoading(false);
        setList(
          update(list, {
            [list.findIndex(item => item._id === exists._id)]: {
              active: { $set: true },
            },
          })
        );
      }
    }

    setSearchValue("");

    if (props.multiple) {
      setSelectedValues([newVal.data]);
    } else {
      setSelectedValues([newVal.data]);
      setShowList(false);
    }
    // setList(newVal.data);
  };

  useEffect(() => {
    // Don't trigger onChange if we're currently updating from props
    if (isUpdatingRef.current || !props.onChange) {
      return;
    }

    if (props.multiple) {
      // Only call onChange if the values are actually different
      const currentValue = props.value;
      const newValue = selectedValues;
      if (JSON.stringify(currentValue) !== JSON.stringify(newValue)) {
        props.onChange(selectedValues);
      }
    } else {
      const currentValue = props.value;
      const newValue = selectedValues.length > 0 ? selectedValues[0]?._id : null;
      if (currentValue !== newValue) {
        if (selectedValues.length > 0) {
          props.onChange(selectedValues[0]?._id);
        } else {
          props.onChange(null);
        }
      }
    }
    // eslint-disable-next-line
  }, [selectedValues]);

  // Effect to handle list changes and validate selected values
  useEffect(() => {
    if (isUpdatingRef.current) {
      return;
    }

    if (list.length > 0 && selectedValuesRef.current.length > 0) {
      // Check if any selected values no longer exist in the list
      const validSelectedValues = selectedValuesRef.current.filter(val =>
        val && list.find(l => l._id === val._id)
      );

      // Only update if the filtered values are actually different
      if (validSelectedValues.length !== selectedValuesRef.current.length) {
        isUpdatingRef.current = true;
        setSelectedValues(validSelectedValues);
        setTimeout(() => { isUpdatingRef.current = false; }, 0);
      }
    }
  }, [list]); // Only depend on list changes

  const updateValue = async () => {
    setLoading(true)
    let re = await axios.put(`/api/validation/${editId}`, {
      label: editValue,
    });
    setLoading(false)
    setList(
      update(list, {
        [list.findIndex(item => item._id === editId)]: {
          $set: re.data,
        },
      })
    );
    setShowConfirmEdit(false);
    setEditId(undefined);
    setEditValue(undefined);
  };

  const mergeWithExisting = async (targetId) => {
    if (!mergeLoading) {
      setMergeLoading(true);
      try {
        // Call API to merge existing data
        await mergeValidation({
          sourceId: editId,
          targetId: targetId,
          facilityId: facilityId,
          accessor: props.accessor,
          accountId: auth.accountId
        });

        // Get the target validation details for updating selected values
        const targetValidation = list.find(item => item._id === targetId);

        // Update selected values to point to the target validation instead of the source
        if (targetValidation && selectedValues.length > 0) {
          const updatedSelectedValues = selectedValues.map(val => {
            // If this was the source validation being edited, replace it with target
            if (val._id === editId) {
              return targetValidation;
            }
            return val;
          });

          setSelectedValues(updatedSelectedValues);

          // Notify parent component about the change
          if (props.onChange) {
            if (props.multiple) {
              props.onChange(updatedSelectedValues);
            } else {
              props.onChange(targetValidation._id);
            }
          }
        }

        // Remove the source item from the list
        setList(previousListData => previousListData.filter((data) => data._id !== editId));

        // Close dialogs and reset state
        resetDynamicCards();
        setShowMergeDialog(false);
        setShowConfirmEdit(false);
        setEditId(undefined);
        setEditValue(undefined);
        setSelectedMergeOption("");

        props.refreshComponent();

      } catch (error) {
        console.error('Error merging validation:', error);
      } finally {
        setMergeLoading(false);
      }
    }
  };

  const moveValidationOption = async (targetQuestionId) => {
    if (!moveLoading) {
      setMoveLoading(true);
      try {
        // We need to get the source question ID from the current context
        // For now, we'll use a placeholder - you may need to pass this as a prop
        const sourceQuestionId = props.sourceQuestionId || props.questionId || "unknown";
        // Call the move API
        const response = await moveValidationOptionApi({
          sourceQuestionId: sourceQuestionId,
          targetQuestionId: targetQuestionId,
          existingValidationId: editId,
          facilityId: facilityId,
          accountId: auth.accountId
        });
        
        if (response?.status === 400) {
          dispatch({
            type: ADD_NOTIFICATION,
            payload: {
              label: response?.error,
              type: "error",
              id: "duplicateValidationOption",
            },
          });
        }
        if (response?.status !== 400) {
          dispatch({
            type: ADD_NOTIFICATION,
            payload: {
              label: response?.message,
              type: "success",
              id: "validationMovedSuccessfully",
            },
          });
          // Close the dialog and reset state
          resetDynamicCards()
          setShowMoveDialog(false);
          setEditId(undefined);
          setEditValue(undefined);
          setSelectedTargetQuestion("");
          props?.refreshComponent();
        }
      } catch (error) {
        console.error('Error moving validation option:', error);
      } finally {
        setMoveLoading(false);
      }
    }
  };

  return (
    <>
      <div className={styles.validation}>
        <div
          className={`df aic ${styles.dummyInputWrpr} ${showList ? styles.open : ""}`}
          style={{
            ...(props.isHighLightInfo && {
              border: `1px solid ${props.isHighLightInfo}`,
              borderRadius: "4px",
            }),
          }}>
          <div
            className={`df aic ${styles.selectedValContainer} ${selectedValues.length === 0 ? styles.empty : ""
              }`}
          >
            {selectedValues.map(val => (
              <p className={`ffmr df aicffmr fs14 ${styles.selectedValue}`} key={val?.label}>
                {val?.label}{" "}
                <span
                  className={`df aic  m-l-5 fs10 cp`}
                  onClick={() => {
                    setSelectedValues(
                      update(selectedValues, {
                        $splice: [[selectedValues.indexOf(val), 1]],
                      })
                    );
                  }}
                >
                  X
                </span>
              </p>
            ))}
          </div>
          {!readOnly && (
            <input
              className={`${props.multiple ? styles.multiple : ""} ${selectedValues.length ? styles.hasValues : ""
                }`}
              onFocus={() => setShowList(true)}
              type="text"
              value={searchValue}
              disabled={getListLoading}
              onChange={e => (selectedValues.length === 0 || props.multiple) && setSearchValue(e.target.value)}
            />
          )}
          <div
            onClick={() => {
              setShowList(!showList);
              setEditId(undefined);
              setEditValue(undefined);
            }}
            className={`${styles.opener} ${showList ? styles.open : ""}`}
          ></div>
        </div>
        {showList ? (
          <div className={`ffmr fs14 ${styles.listWrpr}`}>
            {list
              .filter(
                val =>
                  val.label.toLowerCase().includes(searchValue.toLowerCase()) &&
                  // !isSelected(val._id) &&
                  val.active === true
              )
              .map(item => (
                <p
                  className={`df aic ${styles.line} ${isSelected(item?._id) ? styles.isSelected : ""
                    }`}
                  key={item._id}
                  onClick={e => {
                    e.stopPropagation();
                    setSearchValue("");

                    if (props.multiple) {
                      let isFound = selectedValues?.find(val => val?._id === item._id) || false;
                      if (isFound) {
                        setSelectedValues(
                          update(selectedValues, {
                            $splice: [[selectedValues.indexOf(isFound), 1]],
                          })
                        );
                      } else {
                        setSelectedValues([item]);
                        setShowList(false);
                      }
                    } else {
                      setSelectedValues([item]);
                      setShowList(false);
                    }
                  }}
                >
                  {editId === item._id ? (
                    <div
                      onClick={e => {
                        e.stopPropagation();
                      }}
                      className={`df aic p-l-10 p-r-10 ${styles.editInputWrpr}`}
                    >
                      <input value={editValue} onChange={e => setEditValue(e.target.value)} />
                      <div className={`mla`}>
                        <button
                          className={`${styles.btn}`}
                          onClick={e => {
                            e.stopPropagation();
                            setEditId(undefined);
                            setEditValue(undefined);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className={`${styles.btn} ${styles.convertBtn}`}
                          onClick={() => {
                            setShowMergeDialog(true);
                            setSelectedMergeOption("");
                          }}
                        >
                          Convert
                        </button>
                        <button
                          className={`${styles.btn} ${styles.convertBtn} m-l-5`}
                          onClick={() => {
                            setShowMoveDialog(true);
                            setSelectedTargetQuestion("");
                          }}
                        >
                          Move
                        </button>
                        <button
                          className={`m-l-5 ${styles.btn} ${styles.saveBtn} ${(editValue === item.label || loading) ? styles.disabled : ''}`}
                          disabled={editValue === item.label || loading}
                          onClick={() => setShowConfirmEdit(true)}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : undefined}
                  {item.label}

                  {item.isEditable && !readOnly ? (
                    <div className={`df aic mla`}>
                      <div
                        className={`${styles.editIcon} m-r-10`}
                        onClick={e => {
                          e.stopPropagation();
                          setEditId(item._id);
                          setEditValue(item.label);
                        }}
                      >
                        <EditSVG />
                      </div>
                      <div
                        onClick={e => {
                          e.stopPropagation();
                          setItemToHide(item);
                          setHideConfirmationAnchor(e.currentTarget);
                        }}
                      >
                        <HideSVG />
                      </div>
                    </div>
                  ) : undefined}
                </p>
              ))}
            {searchValue && !readOnly &&
              !list.find(
                elem =>
                  elem.label.toLowerCase().trim() === searchValue.toLowerCase().trim() &&
                  elem.active === true
              ) &&
              !props.autoSave ? (<>
                <p onClick={addValue} className={`${styles.addLine} ${loading ? styles.disabled : null}`}>
                  Add "{searchValue}"
                  {loading && (<span className={styles.addLineLoader}><CircularProgress size={15} /></span>)}
                </p>
              </>
            ) : undefined}
          </div>
        ) : undefined}
      </div>
      {showList ? (
        <div
          className={styles.validationOverlay}
          onClick={() => {
            setSearchValue("");
            setShowList(false);
            setEditId(undefined);
            setEditValue(undefined);
          }}
        ></div>
      ) : undefined}

      {showConfirmEdit ? (
        <div className={styles.confirmUpdateWrpr}>
          <div className={styles.confirmUpdateOverlay}></div>
          <div className={styles.confirmUpdate}>
            <p className={`ffmr fs18 tac`}>

              {list.some(function (el) {
                return el.label === editValue;
              }) ? `Updating value ${editValue} already exist` : (
                `Updating the value will also update all other records containing ${list.find(elem => elem._id === editId).label}" to "${editValue}"`
              )}
            </p>
            <div className={`df acc m-t-15 p-b-25`}>
              <button
                onClick={() => {
                  setShowConfirmEdit(false);
                  setEditId(undefined);
                  setEditValue(undefined);
                }}
                className={styles.btn}
              >
                Cancel
              </button>
              <button onClick={updateValue} disabled={list.some((el) => el.label === editValue)} className={`m-l-10 ${styles.btn} ${styles.updateBtn} ${list.some((el) => el.label === editValue) ? styles.disabled : ''}`}>
                Update
              </button>
            </div>
          </div>
        </div>
      ) : undefined}

      {showMergeDialog ? (
        <div className={styles.confirmUpdateWrpr}>
          <div className={styles.confirmUpdateOverlay}></div>
          <div className={styles.confirmUpdate}>
            <div className={styles.mergeDialogContent}>
              <p className={styles.dialogTitle}>
                Select an existing option to merge with "{editValue}"
              </p>

              <div className={styles.autocompleteContainer}>
                <Autocomplete
                  options={list.filter(item => item._id !== editId && item.active === true)}
                  getOptionLabel={(option) => option.label || ''}
                  value={list.find(item => item._id === selectedMergeOption) || null}
                  onChange={(event, newValue) => {
                    setSelectedMergeOption(newValue ? newValue._id : '');
                  }}
                  disabled={mergeLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select an option..."
                      variant="outlined"
                      inputProps={{
                        ...params.inputProps,
                        className: "customInput"
                      }}
                    />
                  )}
                  sx={{
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                />
              </div>

              <div className={styles.mergeButtonContainer}>
                <button
                  onClick={() => {
                    setShowMergeDialog(false);
                    setEditId(undefined);
                    setEditValue(undefined);
                    setSelectedMergeOption("");
                  }}
                  className={styles.btn}
                  disabled={mergeLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (selectedMergeOption) {
                      // Show confirmation dialog first
                      const targetValidation = list.find(item => item._id === selectedMergeOption);
                      setMergeConfirmationData({
                        sourceLabel: editValue,
                        targetLabel: targetValidation?.label || 'Unknown',
                        sourceId: editId,
                        targetId: selectedMergeOption
                      });
                      setShowMergeConfirmation(true);
                    }
                  }}
                  className={`${styles.btn} ${styles.updateBtn} ${(!selectedMergeOption || mergeLoading) ? styles.disabled : ''}`}
                  disabled={mergeLoading || !selectedMergeOption}
                >
                  {mergeLoading ? 'Merging...' : 'Merge'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : undefined}

      {showMoveDialog ? (
        <div className={styles.confirmUpdateWrpr}>
          <div className={styles.confirmUpdateOverlay}></div>
          <div className={styles.confirmUpdate}>
            <div className={styles.mergeDialogContent}>
              <p className={styles.dialogTitle}>
                Move "{editValue}" to a different question
              </p>

              <div className={styles.moveDialogSection}>
                <label className={styles.moveDialogLabel}>Target Question:</label>
                <div className={styles.autocompleteContainer}>
                  <Autocomplete
                    options={allQuestionsList.filter(q => q.active === true && q._id !== props.questionId)}
                    getOptionLabel={(option) => option?.label || ''}
                    value={allQuestionsList.find(item => item._id === selectedTargetQuestion) || null}
                    onChange={(event, newValue) => {
                      setSelectedTargetQuestion(newValue ? newValue._id : '');
                    }}
                    disabled={moveLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select target question..."
                        variant="outlined"
                        inputProps={{
                          ...params.inputProps,
                          className: "customInput"
                        }}
                      />
                    )}
                    sx={{
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                      }
                    }}
                  />
                </div>
              </div>

              <div className={styles.mergeButtonContainer}>
                <button
                  onClick={() => {
                    setShowMoveDialog(false);
                    setEditId(undefined);
                    setEditValue(undefined);
                    setSelectedTargetQuestion("");
                  }}
                  className={styles.btn}
                  disabled={moveLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (selectedTargetQuestion) {
                      // Show confirmation dialog first
                      const targetQuestion = allQuestionsList.find(q => q._id === selectedTargetQuestion);
                      setMoveConfirmationData({
                        sourceLabel: editValue,
                        targetQuestionLabel: targetQuestion?.label || 'Unknown',
                        sourceId: editId,
                        targetQuestionId: selectedTargetQuestion,
                      });
                      setShowMoveConfirmation(true);
                    }
                  }}
                  className={`${styles.btn} ${styles.updateBtn} ${(!selectedTargetQuestion || moveLoading) ? styles.disabled : ''}`}
                  disabled={moveLoading || !selectedTargetQuestion}
                >
                  {moveLoading ? 'Moving...' : 'Move'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : undefined}

      {/* Merge Confirmation Dialog */}
      {showMergeConfirmation && mergeConfirmationData && (
        <div className={styles.confirmUpdateWrpr}>
          <div className={styles.confirmUpdateOverlay}></div>
          <div className={styles.confirmUpdate}>

            <div className={styles.confirmationContent}>
              <div className={styles.confirmationText}>
                Are you sure you want to merge:
                <p className={styles.sourceLabel}>
                  "{mergeConfirmationData.sourceLabel}"
                </p>
                <p>into:</p>
                <p className={styles.targetLabel}>
                  "{mergeConfirmationData.targetLabel}"
                </p>
                <p className={styles.mutedText}>
                  This action will permanently merge the validation options and cannot be undone.
                </p>
              </div>
              <div className={styles.confirmationButtons}>
                <button
                  onClick={() => {
                    setShowMergeConfirmation(false);
                    setMergeConfirmationData(null);
                  }}
                  className={styles.btn}
                  disabled={mergeLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowMergeConfirmation(false);
                    setMergeConfirmationData(null);
                    mergeWithExisting(mergeConfirmationData.targetId);
                  }}
                  className={`${styles.btn} ${styles.updateBtn} ${mergeLoading ? styles.disabled : ''}`}
                  disabled={mergeLoading}
                >
                  {mergeLoading ? 'Merging...' : 'Confirm Merge'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Move Confirmation Dialog */}
      {showMoveConfirmation && moveConfirmationData && (
        <div className={styles.confirmUpdateWrpr}>
          <div className={styles.confirmUpdateOverlay}></div>
          <div className={styles.confirmUpdate}>
            <div className={styles.confirmationContent}>
              <div className={styles.confirmationText}>
                <p>Are you sure you want to move:</p>
                <p className={styles.sourceLabel}>
                  "{moveConfirmationData.sourceLabel}"
                </p>
                <p>to question:</p>
                <p className={styles.targetLabel}>
                  "{moveConfirmationData.targetQuestionLabel}"
                </p>
                {moveConfirmationData.sourceLabel && (
                  <p className={styles.convertLabel}>
                    If this option is already selected, it will be converted to "{moveConfirmationData.sourceLabel} (converted)"
                  </p>
                )}
                <p className={styles.mutedText}>
                  This action will move the validation option to a different question and cannot be undone.
                </p>
              </div>
              <div className={styles.confirmationButtons}>
                <button
                  onClick={() => {
                    setShowMoveConfirmation(false);
                    setMoveConfirmationData(null);
                  }}
                  className={styles.btn}
                  disabled={moveLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowMoveConfirmation(false);
                    setMoveConfirmationData(null);
                    moveValidationOption(moveConfirmationData.targetQuestionId);
                  }}
                  className={`${styles.btn} ${styles.updateBtn} ${moveLoading ? styles.disabled : ''}`}
                  disabled={moveLoading}
                >
                  {moveLoading ? 'Moving...' : 'Confirm Move'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hide Confirmation Popover */}
      <Popover
        open={Boolean(hideConfirmationAnchor)}
        anchorEl={hideConfirmationAnchor}
        onClose={() => {
          setHideConfirmationAnchor(null);
          setItemToHide(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            minWidth: '280px',
            p: 2
          }
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          {/* <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Confirm Action
          </Typography> */}
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Are you sure you want to remove "{itemToHide?.label}"?
            <br />
            This action cannot be undone.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => {
                setHideConfirmationAnchor(null);
                setItemToHide(null);
              }}
              sx={{ minWidth: '80px' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (itemToHide) {
                  updateValidation(itemToHide._id, { active: false });
                }
                setHideConfirmationAnchor(null);
                setItemToHide(null);
              }}
              sx={{ minWidth: '80px' }}
            >
              Remove
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default Validation;
